/**
 * WorkflowContext — the Phase-1 CRM operating loop.
 *
 * Ports the MFX OS CRM interactive prototype into typed React state:
 *   intake → AI triage → task → calendar → alerts → client profile → audit.
 *
 * This is front-end simulation (no Firebase writes yet) — same intent as the
 * prototype. The live wiring (Firestore + Gmail + Calendar + AI) replaces the
 * generator functions here without changing the components.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type Dept = 'csr' | 'sales' | 'prepress' | 'logistics' | 'production' | 'quality' | 'accounting' | 'operations';
export type TaskStatus = 'assigned' | 'acknowledged' | 'in_progress' | 'blocked' | 'completed';
export type Priority = 'low' | 'standard' | 'rush' | 'critical';
export type IntakeStatus = 'New' | 'Processed';

export interface IntakeItem { id: string; source: string; client: string; summary: string; status: IntakeStatus; task: string; }
export interface WorkTask { id: string; title: string; client: string; owner: string; dept: Dept; status: TaskStatus; priority: Priority; due: string; description: string; }
export interface CalEvent { time: string; day: string; title: string; dept: Dept; }
export interface TimelineItem { time: string; title: string; text: string; }
export interface AuditItem { title: string; detail: string; time: string; }
export interface Toast { title: string; msg: string; }

interface WorkflowState {
  intake: IntakeItem[];
  tasks: WorkTask[];
  events: CalEvent[];
  timeline: TimelineItem[];
  audit: AuditItem[];
  digest: string;
  intakeAi: string;
  agentConversation: string;
  flowStep: number;          // 1..6
  toast: Toast | null;
  // derived
  metrics: { openIntake: number; activeTasks: number; blocked: number; risk: number };
  // actions
  submitIntake: (f: { source: string; client: string; contact?: string; email?: string; text: string }) => string;
  ackTask: (id: string) => void;
  moveProgress: (id: string) => void;
  blockTask: (id: string) => void;
  generateDigest: () => void;
  askAgents: (q: string, a1: string, a2: string) => void;
  runDemo: () => void;
  simulateOverdue: () => void;
  addCalendarDemo: () => void;
  addAudit: (title: string, detail: string) => void;
  reset: () => void;
  setFlowStep: (n: number) => void;
}

const statusLabel = (s: string) => s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SEED = () => ({
  intake: [
    { id: 'INT-2026-0001', source: 'Website Form', client: 'Acme Nutraceutical Packaging', summary: 'Requested pricing for 10K nutraceutical pouches.', status: 'Processed' as IntakeStatus, task: 'TASK-2026-1001' },
    { id: 'INT-2026-0002', source: 'Email', client: 'Blue Harbor Foods', summary: 'Asked for artwork proof update and delivery timing.', status: 'Processed' as IntakeStatus, task: 'TASK-2026-1002' },
    { id: 'INT-2026-0003', source: 'Phone Call', client: 'Acme Nutraceutical Packaging', summary: 'Needs samples for three SKUs by Friday; SKU numbers unclear.', status: 'New' as IntakeStatus, task: '—' },
    { id: 'INT-2026-0004', source: 'Text Message', client: 'Peak Wellness', summary: 'Checking invoice status and tracking number.', status: 'New' as IntakeStatus, task: '—' },
  ] as IntakeItem[],
  tasks: [
    { id: 'TASK-2026-1001', title: 'Prepare quote for 10K nutraceutical pouches', client: 'Acme Nutraceutical Packaging', owner: 'Fatima', dept: 'sales', status: 'in_progress', priority: 'rush', due: 'Thu 2:00 PM', description: 'Quote request from website form. Confirm material and MOQ before sending.' },
    { id: 'TASK-2026-1002', title: 'Review updated artwork proof', client: 'Blue Harbor Foods', owner: 'Diego', dept: 'prepress', status: 'assigned', priority: 'standard', due: 'Wed 4:00 PM', description: 'Client sent artwork update. Compare to previous approved proof.' },
    { id: 'TASK-2026-1003', title: 'Confirm material receiving date', client: 'Acme Nutraceutical Packaging', owner: 'Kim', dept: 'logistics', status: 'blocked', priority: 'standard', due: 'Fri 10:00 AM', description: 'Vendor confirmation pending for roll stock receiving.' },
    { id: 'TASK-2026-1004', title: 'Run quality release check', client: 'Peak Wellness', owner: 'Marisol', dept: 'quality', status: 'acknowledged', priority: 'standard', due: 'Today 3:00 PM', description: 'Final release check needed before shipment.' },
  ] as WorkTask[],
  events: [
    { time: '9 AM', day: 'Mon', title: 'CSR follow-up: Acme quote', dept: 'sales' },
    { time: '11 AM', day: 'Tue', title: 'Prepress proof review', dept: 'prepress' },
    { time: '2 PM', day: 'Wed', title: 'Material receiving check', dept: 'logistics' },
    { time: '3 PM', day: 'Thu', title: 'Quality release review', dept: 'quality' },
  ] as CalEvent[],
  timeline: [
    { time: 'Today 9:14 AM', title: 'Phone intake created', text: 'Jane requested samples for three SKUs by Friday. SKU numbers unclear.' },
    { time: 'Yesterday 4:22 PM', title: 'Quote Q-2026-000789 sent', text: '10K pouch quote sent through client portal. Waiting on approval.' },
    { time: 'Yesterday 2:05 PM', title: 'Material status updated', text: 'Logistics waiting on vendor confirmation for roll stock receiving date.' },
    { time: 'Mon 10:30 AM', title: 'Proof approved', text: 'Blueprint BP-SKU-ACME-0007-V03 marked approved for reorder.' },
  ] as TimelineItem[],
  audit: [
    { title: 'Task created from website intake', detail: 'TASK-2026-1001 linked to INT-2026-0001 and Acme CRM profile.', time: 'Today 8:40 AM' },
    { title: 'Calendar event synced', detail: 'Quote deadline added to Sales Quote Calendar.', time: 'Today 8:41 AM' },
    { title: 'AI summary generated', detail: 'MFX Intake AI classified request as quote / rush / sales.', time: 'Today 8:41 AM' },
    { title: 'Task blocked', detail: 'TASK-2026-1003 marked blocked: waiting on vendor confirmation.', time: 'Yesterday 2:05 PM' },
  ] as AuditItem[],
});

const Ctx = createContext<WorkflowState | null>(null);

export function WorkflowProvider({ children }: { children: ReactNode }) {
  const seed = SEED();
  const [intake, setIntake] = useState(seed.intake);
  const [tasks, setTasks] = useState(seed.tasks);
  const [events, setEvents] = useState(seed.events);
  const [timeline, setTimeline] = useState(seed.timeline);
  const [audit, setAudit] = useState(seed.audit);
  const [digest, setDigest] = useState('Waiting for digest. Select “Generate AI Digest” or run the automation demo.');
  const [intakeAi, setIntakeAi] = useState('Submit the intake to simulate AI classification, missing-field detection, task creation, notification, and calendar sync.');
  const [agentConversation, setAgentConversation] = useState('Agent conversation output will appear here.');
  const [flowStep, setFlowStep] = useState(1);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((title: string, msg: string) => {
    setToast({ title, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3600);
  }, []);

  const addAudit = useCallback((title: string, detail: string) => {
    setAudit((a) => [{ title, detail, time: 'Just now' }, ...a]);
  }, []);
  const addTimeline = useCallback((title: string, text: string) => {
    setTimeline((t) => [{ time: 'Just now', title, text }, ...t]);
  }, []);

  const submitIntake: WorkflowState['submitIntake'] = useCallback((f) => {
    let newTaskId = '';
    setIntake((prevIntake) => {
      const id = `INT-2026-${String(prevIntake.length + 1).padStart(4, '0')}`;
      setTasks((prevTasks) => {
        newTaskId = `TASK-2026-${1000 + prevTasks.length + 1}`;
        const summary = f.text.length > 150 ? f.text.slice(0, 147) + '...' : f.text;
        setIntake((cur) => [{ id, source: f.source, client: f.client || 'Unknown Client', summary, status: 'Processed', task: newTaskId }, ...cur.filter((x) => x.id !== id)]);
        const newTask: WorkTask = {
          id: newTaskId, title: 'Confirm SKUs and prepare sample request', client: f.client || 'Unknown Client',
          owner: 'Fatima', dept: 'csr', status: 'assigned', priority: 'rush', due: 'Fri 11:00 AM',
          description: 'AI classified this as a sample request. Missing data: exact SKU numbers. Recommended action: email the client today to confirm SKUs, then route to Logistics.',
        };
        return [newTask, ...prevTasks];
      });
      const summary = f.text.length > 150 ? f.text.slice(0, 147) + '...' : f.text;
      return [{ id, source: f.source, client: f.client || 'Unknown Client', summary, status: 'Processed', task: newTaskId }, ...prevIntake];
    });
    setEvents((e) => [...e, { time: '11 AM', day: 'Fri', title: 'Due: sample request', dept: 'csr' }]);
    setIntakeAi(
      `AI TRIAGE RESULT\n\nTitle: Confirm SKUs and prepare sample request\nRequest Type: Sample Request\nPriority: Rush\nDepartment: CSR / Client Services\nAssigned Owner: Fatima\nMissing Fields: Exact SKU numbers\nClient Action Needed: Yes\nDue Date: Friday 11:00 AM\n\nRecommended Next Action:\nSend the client a confirmation email today asking which three SKUs match the last reorder. Once confirmed, route a logistics sample-prep task.\n\nAutomations Executed:\n✓ Intake record created\n✓ Client profile updated\n✓ Task created\n✓ Staff notification sent\n✓ Calendar event created\n✓ Audit log written`,
    );
    addAudit('AI triage and task automation completed', `Intake converted into a task, assigned to Fatima; calendar synced for Friday 11:00 AM.`);
    addTimeline('Sample request task created', `Task assigned to Fatima. AI flagged missing SKU numbers and recommended client confirmation.`);
    setFlowStep(6);
    showToast('Automation Complete', 'Intake was classified, assigned, calendar-synced, and written to the client profile.');
    return newTaskId;
  }, [addAudit, addTimeline, showToast]);

  const ackTask = useCallback((id: string) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'acknowledged' } : t)));
    const t = tasks.find((x) => x.id === id);
    addAudit('Task acknowledged', `${id} acknowledged${t ? ` by ${t.owner}` : ''}. Ownership confirmed.`);
    showToast('Task Acknowledged', `${t?.owner ?? 'Owner'} accepted ownership of ${id}.`);
  }, [tasks, addAudit, showToast]);

  const moveProgress = useCallback((id: string) => {
    let label = '';
    setTasks((ts) => ts.map((t) => {
      if (t.id !== id) return t;
      let s: TaskStatus = t.status;
      if (t.status === 'assigned') s = 'acknowledged';
      else if (t.status === 'acknowledged') s = 'in_progress';
      else if (t.status === 'in_progress' || t.status === 'blocked') s = 'completed';
      label = statusLabel(s);
      return { ...t, status: s };
    }));
    addAudit('Task status updated', `${id} moved to ${label}.`);
    showToast('Status Updated', `${id} is now ${label}.`);
  }, [addAudit, showToast]);

  const blockTask = useCallback((id: string) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, status: 'blocked' } : t)));
    addAudit('Task blocked', `${id} blocked. Reason required: waiting on information/material/approval.`);
    showToast('Task Blocked', `${id} is now blocked and visible to Operations.`);
  }, [addAudit, showToast]);

  const generateDigest = useCallback(() => {
    const open = intake.filter((i) => i.status !== 'Processed').length;
    const active = tasks.filter((t) => t.status !== 'completed').length;
    const blk = tasks.filter((t) => t.status === 'blocked').length;
    setDigest(
      `MFX OS CHIEF AI DIGEST\n\n1. Intake: ${open} open intake items remain. Highest-priority open item is Acme’s sample request, which requires SKU confirmation.\n\n2. Tasks: ${active} active tasks. ${blk} blocked. Operations should review blocked logistics and prepress tasks before end of day.\n\n3. Calendar: Upcoming commitments are visible across CSR, Sales, Logistics, Prepress, and Quality. Friday sample deadline should remain monitored.\n\n4. Recommended Action: Have CSR confirm SKU numbers, then trigger Logistics sample-prep task. If no client response by 2 PM, send automated follow-up and flag Operations.`,
    );
    addAudit('AI digest generated', 'OS Chief AI generated dashboard-level risk and next-action summary.');
    showToast('AI Digest Ready', 'Dashboard digest has been updated.');
  }, [intake, tasks, addAudit, showToast]);

  const askAgents = useCallback((q: string, a1: string, a2: string) => {
    setAgentConversation(
      `${a1}: I reviewed the CRM profile, task board, and intake record. The request is active and not blocked by production. The key missing item is exact SKU confirmation from the client.\n\n${a2}: From my scoped department view, the next step is to confirm the three SKUs before preparing samples. If SKUs match the last reorder, Logistics can use the SKU history to prepare the correct sample set.\n\nMFX OS Recommendation:\n1. CSR sends the client a SKU confirmation email today.\n2. System keeps the Friday deadline on the CSR calendar.\n3. If the client replies, create a Logistics sample-prep task automatically.\n4. If no reply by 2 PM, trigger follow-up reminder and escalate to Operations if the due date is at risk.\n\nQuestion asked: ${q}`,
    );
    addAudit('Multi-agent conversation generated', `${a1} and ${a2} reviewed the request and produced a coordinated recommendation.`);
  }, [addAudit]);

  const simulateOverdue = useCallback(() => {
    setTasks((ts) => {
      if (!ts.length) return ts;
      const [first, ...rest] = ts;
      addAudit('Escalation triggered', `${first.id} is overdue and critical. Escalation sent to Operations.`);
      return [{ ...first, status: 'blocked', priority: 'critical', due: 'Overdue' }, ...rest];
    });
    showToast('Escalation Triggered', 'A critical overdue task is now visible to Operations and Leadership.');
  }, [addAudit, showToast]);

  const addCalendarDemo = useCallback(() => {
    setEvents((e) => [...e, { time: '10 AM', day: 'Fri', title: 'Operations escalation review', dept: 'operations' }]);
    addAudit('Calendar event created', 'Operations escalation review added to MFX Master Operations Calendar.');
    showToast('Calendar Synced', 'New shared calendar event created.');
  }, [addAudit, showToast]);

  const runDemo = useCallback(() => {
    setFlowStep(1);
    const steps = [2, 3, 4, 5];
    steps.forEach((n, i) => setTimeout(() => setFlowStep(n), 350 * (i + 1)));
    setTimeout(() => {
      submitIntake({ source: 'Phone Call', client: 'Acme Nutraceutical Packaging', text: 'Jane called and needs samples for three nutraceutical pouch SKUs by Friday. She is not sure which SKU numbers, but says they match the last reorder.' });
    }, 1750);
  }, [submitIntake]);

  const reset = useCallback(() => {
    const s = SEED();
    setIntake(s.intake); setTasks(s.tasks); setEvents(s.events);
    setTimeline(s.timeline); setAudit(s.audit);
    setDigest('Waiting for digest. Select “Generate AI Digest” or run the automation demo.');
    setIntakeAi('Submit the intake to simulate AI classification, missing-field detection, task creation, notification, and calendar sync.');
    setAgentConversation('Agent conversation output will appear here.');
    setFlowStep(1);
    showToast('Demo Reset', 'Sample state restored.');
  }, [showToast]);

  const metrics = useMemo(() => ({
    openIntake: intake.filter((i) => i.status !== 'Processed').length,
    activeTasks: tasks.filter((t) => t.status !== 'completed').length,
    blocked: tasks.filter((t) => t.status === 'blocked').length,
    risk: 74,
  }), [intake, tasks]);

  const value: WorkflowState = {
    intake, tasks, events, timeline, audit, digest, intakeAi, agentConversation, flowStep, toast, metrics,
    submitIntake, ackTask, moveProgress, blockTask, generateDigest, askAgents, runDemo, simulateOverdue,
    addCalendarDemo, addAudit, reset, setFlowStep,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkflow() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useWorkflow must be used within WorkflowProvider');
  return c;
}

export const labels = { status: statusLabel, dept: (d: string) => d.replace(/\b\w/g, (c) => c.toUpperCase()) };
